<template>
  <ReportPanel>
    <h6 class="q-mt-md">Select a quarter or custom date range to report.</h6>

    <ReportPeriodSelector
      :period-type="periodType"
      :period-options="periodOptions"
      :start-date="startDate"
      :end-date="endDate"
      @update:period-type="periodType = $event"
      @update:start-date="startDate = $event"
      @update:end-date="endDate = $event"
    />

    <FinancialYearQuarterSelector
      v-if="periodType === 'quarter'"
      :selected-financial-year="selectedFinancialYear"
      :selected-quarter="selectedQuarter"
      :financial-years="financialYears"
      :quarter-options="quarterOptions"
      :show-quarter="true"
      @update:selected-financial-year="selectedFinancialYear = $event"
      @update:selected-quarter="selectedQuarter = $event"
    />

    <q-btn
      :loading="loading"
      label="Generate Ko Tātou Report"
      class="full-width"
      color="secondary"
      no-caps
      @click="generateReport"
    />
  </ReportPanel>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import { promptDownload } from "@/services/util/file";
import FinancialYearQuarterSelector from "./FinancialYearQuarterSelector.vue";
import ReportPanel from "./ReportPanel.vue";
import ReportPeriodSelector from "./ReportPeriodSelector.vue";

function getCurrentFinancialYearStart() {
  const today = new Date();
  return today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1;
}

export default {
  name: "KoTatouReportPanel",
  components: {
    FinancialYearQuarterSelector,
    ReportPanel,
    ReportPeriodSelector
  },
  props: {
    financialYears: {
      type: Array,
      required: true
    },
    quarterOptions: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      periodType: "quarter",
      periodOptions: [
        { label: "Quarter", value: "quarter" },
        { label: "Custom Dates", value: "custom" }
      ],
      selectedFinancialYear: getCurrentFinancialYearStart(),
      selectedQuarter: null,
      startDate: null,
      endDate: null
    }
  },
  methods: {
    async generateReport() {
      this.loading = true;
      try {
        if (this.periodType === "quarter") {
          await this.generateQuarterReport();
        } else {
          await this.generateCustomReport();
        }
      } catch (error) {
        console.error(error);
        if (error.isValidationError) {
          notify.error(error.message, "top");
        } else {
          notify.error("There was an issue generating the Ko Tātou report.", "top");
        }
      } finally {
        this.loading = false;
      }
    },

    async generateQuarterReport() {
      if (this.selectedQuarter === null) {
        const error = new Error("Please select a quarter.");
        error.isValidationError = true;
        throw error;
      }
      const report = await api.reports.generateKoTatouReport(
        this.selectedQuarter,
        this.selectedFinancialYear
      );
      promptDownload(
        report,
        `Ko Tatou Report FY${this.selectedFinancialYear}-${this.selectedFinancialYear + 1} Q${this.selectedQuarter}.xlsx`
      );
    },

    async generateCustomReport() {
      if (!this.startDate || !this.endDate) {
        const error = new Error("Please select a start date and end date.");
        error.isValidationError = true;
        throw error;
      }
      if (this.startDate > this.endDate) {
        const error = new Error("The start date must be on or before the end date.");
        error.isValidationError = true;
        throw error;
      }
      const report = await api.reports.generateKoTatouCustomReport(this.startDate, this.endDate);
      promptDownload(report, `Ko Tatou Report ${this.startDate} to ${this.endDate}.xlsx`);
    }
  }
}
</script>
